import { useState } from 'react';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export function Pagination({ page, pageSize, total, onChange, onPageSizeChange }: PaginationProps) {
  const [jumpInput, setJumpInput] = useState('');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  // Si la lista se achicó y `page` quedó después de la última, el rango es el de la última (nunca "51–50").
  const enRango = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (enRango - 1) * pageSize + 1;
  const to = Math.min(enRango * pageSize, total);

  const handleJump = () => {
    const n = parseInt(jumpInput);
    if (n >= 1 && n <= totalPages) { onChange(n); setJumpInput(''); }
  };

  const pages: (number | string)[] = [];
  if (totalPages <= 10) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-3 text-sm" role="navigation" aria-label="Paginación">
      <div className="flex items-center gap-4">
        <span className="text-slate-500">Mostrando {from}–{to} de {total.toLocaleString()}</span>
        {onPageSizeChange && (
          <select value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}
            className="px-2 py-1 border rounded text-xs text-slate-600" aria-label="Registros por página">
            <option value={20}>20</option><option value={50}>50</option><option value={100}>100</option>
          </select>
        )}
      </div>
      <div className="flex items-center gap-2 overflow-x-auto max-w-full py-1" role="group">
        <button onClick={() => onChange(page - 1)} disabled={page <= 1}
          className="min-h-[2.5rem] px-2 py-1 rounded border disabled:opacity-30 hover:bg-slate-100 transition-colors shrink-0" aria-label="Anterior">←</button>
        {pages.map((p, i) =>
          typeof p === 'number' ? (
            <button key={i} onClick={() => onChange(p)}
              className={`min-w-[2.5rem] min-h-[2.5rem] px-2 py-1 rounded border transition-colors shrink-0 ${p === page ? 'bg-brand-600 text-white border-brand-600' : 'hover:bg-slate-100'}`}
              aria-label={`Página ${p}`} aria-current={p === page ? 'page' : undefined}>{p}</button>
          ) : (
            <span key={i} className="px-1 text-slate-400 shrink-0">…</span>
          )
        )}
        <button onClick={() => onChange(page + 1)} disabled={page >= totalPages}
          className="min-h-[2.5rem] px-2 py-1 rounded border disabled:opacity-30 hover:bg-slate-100 transition-colors shrink-0" aria-label="Siguiente">→</button>
        {totalPages > 20 && (
          <div className="flex items-center gap-1 ml-2 shrink-0">
            <input value={jumpInput} onChange={e => setJumpInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleJump()}
              placeholder={`1-${totalPages}`} className="w-14 min-h-[2.5rem] px-2 py-1 border rounded text-xs text-center"
              aria-label="Ir a página" />
            <button onClick={handleJump} className="min-h-[2.5rem] px-2 py-1 rounded border text-xs hover:bg-slate-100">Ir</button>
          </div>
        )}
      </div>
    </div>
  );
}
